import { Button, Text } from '@bancobarigui/barihaus';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';

import { getInspectionItemStatusInfo } from '@/shared/status';
import { UserPermissions } from '@/shared/enums/UserPermissions';
import { InspectionIssueStatus } from '@/services/types/inspection.types';
import { InspectionTableData } from './useInspectionsTable';
import { StatusBadge } from '@/components/StatusBadge';
import { generateActionMenuItems } from './ActionMenuItems';

interface ExpandableRowProps {
  record: InspectionTableData;
  hasPermission: (permissions: UserPermissions[]) => boolean;
  isInspectorUser: () => boolean;
  onProviderAccept?: (assignmentId: string, inspectionId: string) => void;
  onProviderReject?: (assignmentId: string, inspectionId: string) => void;
  onAssignInspector?: (assignmentId: string, inspectionId: string) => void;
  onInspectorAccept?: (assignmentId: string, inspectionId: string) => void;
  onInspectorReject?: (assignmentId: string, inspectionId: string) => void;
  onCreateIssue?: (itemId: string) => void;
  onSubmitProviderReport?: (itemId: string, inspectionId: string) => void;
  onSchedule: (itemId: string, inspectionId: string) => void;
  onAssign: (itemId: string, inspectionId: string) => void;
  onRowClick?: (itemId: string, inspectionId: string) => void;
}

export function ExpandableRow({
  record,
  hasPermission,
  isInspectorUser,
  onProviderAccept,
  onProviderReject,
  onAssignInspector,
  onInspectorAccept,
  onInspectorReject,
  onCreateIssue,
  onSubmitProviderReport,
  onSchedule,
  onAssign,
  onRowClick,
}: ExpandableRowProps) {
  if (!record.items || record.items.length <= 1) return null;

  return (
    <div className="bg-gray-50 p-4">
      {record.items.map((item, index) => {
        const itemId = item.id || `${record.id}-${index}`;

        const itemMenuItems: MenuProps['items'] = generateActionMenuItems({
          record,
          itemIndex: index,
          hasPermission,
          isInspectorUser,
          onProviderAccept,
          onProviderReject,
          onAssignInspector,
          onInspectorAccept,
          onInspectorReject,
          onCreateIssue,
          onSubmitProviderReport,
          onSchedule,
          onAssign,
        });

        const itemStatusInfo =
          item.status !== undefined ? getInspectionItemStatusInfo(item.status) : null;

        const itemHasActiveIssues = item.issues && item.issues.length > 0 &&
          item.issues.some(issue =>
            issue.status === InspectionIssueStatus.Aberta ||
            issue.status === InspectionIssueStatus.EmAndamento
          );

        return (
          <div
            key={index}
            className={`flex items-center gap-4 py-3 px-4 bg-white mb-2 rounded border hover:bg-gray-50 cursor-pointer ${
              itemHasActiveIssues ? 'border-orange-400 shadow-sm text-orange-600' : 'border-gray-200'
            }`}
            onClick={() => onRowClick?.(itemId, record.id)}
            title={itemHasActiveIssues ? 'Este item possui pendências ativas' : undefined}
          >
            <div className="flex-1 grid grid-cols-4 gap-4">
              <div>
                <Text className="text-xs text-gray-500 block">Endereço</Text>
                <Text className="text-sm font-medium">
                  {item.street}, {item.number}
                  {item.complement ? ` - ${item.complement}` : ''}
                </Text>
              </div>
              <div>
                <Text className="text-xs text-gray-500 block">Bairro</Text>
                <Text className="text-sm font-medium">{item.district}</Text>
              </div>
              <div>
                <Text className="text-xs text-gray-500 block">Cidade/UF</Text>
                <Text className="text-sm font-medium">
                  {item.city}/{item.state}
                </Text>
              </div>
              <div>
                <Text className="text-xs text-gray-500 block">Status</Text>
                {itemStatusInfo ? (
                  <StatusBadge label={itemStatusInfo.label} color={itemStatusInfo.color} />
                ) : (
                  <Text className="text-sm font-medium">-</Text>
                )}
              </div>
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              {itemMenuItems && itemMenuItems.length > 0 ? (
                <Dropdown
                  menu={{ items: itemMenuItems }}
                  placement="bottomRight"
                  trigger={['click']}
                  overlayClassName="inspection-actions-dropdown"
                >
                  <Button type="link" icon={<MoreOutlined />} className="!p-0">
                    Opções
                  </Button>
                </Dropdown>
              ) : (
                <Text className="text-gray-400 text-xs">Sem ações disponíveis</Text>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
